const PICKER_SELECTOR = '[data-saved-card-picker]';
const CHIP_SELECTOR = '.saved-card-chip';
const TRACK_SELECTOR = '.saved-card-picker__track';

let brandCatalog = null;
let listenersBound = false;
let wheelFrame = null;
let wheelTrack = null;
let pendingWheelScroll = 0;

function getBrand(cardType) {
    if (brandCatalog === null) {
        const el = document.getElementById('card-brand-catalog');
        if (!el) {
            throw new Error('getBrand: #card-brand-catalog script tag is missing from the page');
        }
        brandCatalog = JSON.parse(el.textContent);
    }
    return brandCatalog[cardType] || null;
}

function resolvePicker(target) {
    if (!target) return null;
    if (typeof target === 'string') return document.querySelector(`[data-saved-card-picker="${target}"]`);
    if (target.jquery) return target.get(0) || null;
    return target.closest(PICKER_SELECTOR);
}

function isDefaultCard(card) {
    return card.default === 1 || card.default === true || card.default === '1';
}

function buildOtpIcon(otpTooltip) {
    const icon = document.createElement('i');
    icon.className = 'fa-solid fa-shield-halved saved-card-chip__otp';
    icon.setAttribute('data-toggle', 'tooltip');
    icon.setAttribute('title', otpTooltip);
    return icon;
}

function buildChip(card, otpTooltip) {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'saved-card-chip';
    chip.setAttribute('role', 'radio');
    chip.setAttribute('aria-checked', 'false');
    chip.dataset.savedCardId = card.id;
    chip.dataset.ccType = card.cc_type || '';

    const brand = getBrand(card.cc_type);
    const lastFour = String(card.cc_info || '').slice(-4);
    const expiry = `${card.exp_month || ''}/${String(card.exp_year || '').slice(-2)}`;
    const isDefault = isDefaultCard(card);
    const needsOtp = card.use_otp === 1 || card.use_otp === true;

    chip.innerHTML = `
        <span class="saved-card-chip__flags">
            <span class="saved-card-chip__check"><i class="fa-solid fa-check" aria-hidden="true"></i></span>
        </span>
        <span class="saved-card-chip__brand">${brand ? `<img src="${brand.imageUrl}" class="saved-card-chip__brand-image" alt="${brand.label}">` : ''}</span>
        <span class="saved-card-chip__digits">&bull;&bull;&bull;&bull; ${lastFour}</span>
        <span class="saved-card-chip__meta">
            ${expiry}
            ${isDefault ? '<span class="saved-card-chip__badge">Default</span>' : ''}
        </span>`;

    if (needsOtp) {
        chip.querySelector('.saved-card-chip__flags').prepend(buildOtpIcon(otpTooltip));
    }

    return chip;
}

function buildAddCardChip() {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'saved-card-chip saved-card-chip--add';
    chip.dataset.addCard = '1';
    chip.setAttribute('aria-label', 'Add a new card');
    chip.innerHTML = `
        <span class="saved-card-chip__add-icon"><i class="fa-regular fa-plus" aria-hidden="true"></i></span>
        <span class="saved-card-chip__add-label">Add card</span>`;
    return chip;
}

function wheelDelta(event, track) {
    const raw = Math.abs(event.deltaY) > Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
    if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return raw * 16;
    if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return raw * track.clientWidth;
    return raw;
}

function queueWheelScroll(track, delta) {
    if (wheelTrack !== track) {
        wheelTrack = track;
        pendingWheelScroll = 0;
    }
    pendingWheelScroll += delta;
    if (wheelFrame !== null) return;

    wheelFrame = requestAnimationFrame(() => {
        wheelFrame = null;
        wheelTrack.scrollLeft += pendingWheelScroll;
        pendingWheelScroll = 0;
    });
}

let trackResizeObserver = null;

function updateScrollState(picker) {
    const track = picker.querySelector(TRACK_SELECTOR);
    const [prev, next] = picker.querySelectorAll('[data-saved-card-scroll]');
    if (!track || !prev || !next) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft >= maxScroll - 1;

    picker.classList.toggle('is-scrollable', maxScroll > 1);
    picker.classList.toggle('is-at-start', atStart);
    picker.classList.toggle('is-at-end', atEnd);
    prev.disabled = atStart;
    next.disabled = atEnd;
}

function observeTrack(track) {
    if (!track || track.dataset.scrollObserved === '1') return;
    track.dataset.scrollObserved = '1';
    if (!trackResizeObserver) {
        trackResizeObserver = new ResizeObserver((entries) => {
            entries.forEach((entry) => {
                const picker = entry.target.closest(PICKER_SELECTOR);
                if (picker) updateScrollState(picker);
            });
        });
    }
    trackResizeObserver.observe(track);
}

function scheduleScrollState(picker) {
    updateScrollState(picker);
    requestAnimationFrame(() => {
        updateScrollState(picker);
        requestAnimationFrame(() => updateScrollState(picker));
    });
}

function chipTooltips(track) {
    return typeof $.fn.tooltip === 'function' ? $(track).find('[data-toggle="tooltip"]') : null;
}

function setAddCardChipActive(picker, isActive) {
    const addChip = picker.querySelector('.saved-card-chip--add');
    if (!addChip) return;

    addChip.classList.toggle('is-active', isActive);
    addChip.setAttribute('aria-label', isActive ? 'Cancel and use saved card' : 'Add a new card');
    const label = addChip.querySelector('.saved-card-chip__add-label');
    if (label) label.textContent = isActive ? 'Close' : 'Add card';
}

function setSelectedChip(picker, chip) {
    picker.querySelectorAll(CHIP_SELECTOR).forEach((other) => {
        const isSelected = other === chip;
        other.classList.toggle('is-selected', isSelected);
        other.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });
    setAddCardChipActive(picker, !chip);

    const input = picker.querySelector('input[type="hidden"]');
    if (!input) return;

    input.value = chip ? chip.dataset.savedCardId : '';
    $(input).trigger('change');
}

export function renderSavedCards(target, cards, selectedId) {
    const picker = resolvePicker(target);
    if (!picker) return;

    const list = [...(Array.isArray(cards) ? cards : [])].sort(
        (a, b) => (isDefaultCard(b) ? 1 : 0) - (isDefaultCard(a) ? 1 : 0)
    );
    const track = picker.querySelector(TRACK_SELECTOR);
    chipTooltips(track)?.tooltip('dispose');
    track.innerHTML = '';
    const otpTooltip = picker.dataset.otpTooltip || 'Requires a one-time PIN';
    const showAddCard = picker.dataset.showAddCard === '1';
    if (showAddCard) track.appendChild(buildAddCardChip());
    list.forEach((card) => track.appendChild(buildChip(card, otpTooltip)));

    picker.querySelector('.saved-card-picker__empty').hidden = list.length > 0;
    chipTooltips(track)?.tooltip({ container: 'body', trigger: 'hover', boundary: 'window' });
    const chipOffset = showAddCard ? 1 : 0;
    const defaultChip = track.children[chipOffset] || null;
    if (defaultChip?.dataset.savedCardId) {
        picker.dataset.defaultCardId = defaultChip.dataset.savedCardId;
    } else {
        delete picker.dataset.defaultCardId;
    }
    const selectedKey = String(selectedId ?? '').replace(/\D/g, '');
    const preferredChip = selectedKey
        ? track.querySelector(`${CHIP_SELECTOR}[data-saved-card-id="${selectedKey}"]`)
        : null;
    if (picker.dataset.clearInitial === '1') {
        setSelectedChip(picker, null);
    } else {
        setSelectedChip(picker, preferredChip || defaultChip);
    }
    observeTrack(track);
    scheduleScrollState(picker);
}

export function setSavedCardsDisabled(target, disabled) {
    const picker = resolvePicker(target);
    if (!picker) return;

    picker.classList.toggle('is-disabled', disabled);
    picker.querySelectorAll(CHIP_SELECTOR).forEach((chip) => {
        chip.disabled = disabled;
    });
}

export function clearSavedCardSelection(target) {
    const picker = resolvePicker(target) || document.querySelector(PICKER_SELECTOR);
    if (!picker) return;

    setSelectedChip(picker, null);
}

export function refreshSavedCardScroll(root) {
    const el = root?.jquery ? root.get(0) : root;
    if (!el || typeof el.querySelectorAll !== 'function') return;
    el.querySelectorAll(PICKER_SELECTOR).forEach(scheduleScrollState);
}

function renderPending(root) {
    root.querySelectorAll(`${PICKER_SELECTOR}[data-cards]`).forEach((picker) => {
        const cards = JSON.parse(picker.dataset.cards || '[]');
        delete picker.dataset.cards;
        renderSavedCards(picker, cards);
    });
}

function initSavedCardPicker() {
    if (listenersBound) return;
    if (document.documentElement.dataset.savedCardPickerBound === '1') {
        listenersBound = true;
        return;
    }
    document.documentElement.dataset.savedCardPickerBound = '1';
    listenersBound = true;

    document.addEventListener('saved-card:clear', () => {
        clearSavedCardSelection();
    });

    document.addEventListener('click', (event) => {
        const chip = event.target.closest(CHIP_SELECTOR);
        if (chip) {
            const picker = chip.closest(PICKER_SELECTOR);
            if (picker?.classList.contains('is-disabled') || chip.disabled) return;

            if (chip.dataset.addCard === '1') {
                const input = picker?.querySelector('input[type="hidden"]');
                const hasSelection = Boolean(input && input.value);

                if (!hasSelection) {
                    const defaultChip = picker?.dataset.defaultCardId
                        ? picker.querySelector(`${CHIP_SELECTOR}[data-saved-card-id="${picker.dataset.defaultCardId}"]`)
                        : null;
                    if (defaultChip) {
                        setSelectedChip(picker, defaultChip);
                        return;
                    }
                    document.dispatchEvent(new CustomEvent('saved-card:add-new', { detail: { picker } }));
                    return;
                }
                document.dispatchEvent(new CustomEvent('saved-card:add-new', { detail: { picker } }));
                return;
            }

            if (
                picker?.dataset.payOnReselect === '1'
                && chip.classList.contains('is-selected')
                && chip.dataset.savedCardId
            ) {
                document.dispatchEvent(new CustomEvent('saved-card:pay', {
                    detail: { cardId: chip.dataset.savedCardId, picker },
                }));
                return;
            }

            if (
                picker?.dataset.allowDeselect === '1'
                && chip.classList.contains('is-selected')
            ) {
                setSelectedChip(picker, null);
                return;
            }

            setSelectedChip(picker, chip);
            return;
        }

        const arrow = event.target.closest('[data-saved-card-scroll]');
        if (!arrow) return;

        const picker = arrow.closest(PICKER_SELECTOR);
        const track = picker.querySelector(TRACK_SELECTOR);
        const step = Math.max(track.clientWidth * 0.8, 160);
        track.scrollBy({ left: arrow.dataset.savedCardScroll === 'prev' ? -step : step, behavior: 'smooth' });
    });

    document.addEventListener('scroll', (event) => {
        const track = event.target.closest ? event.target.closest(TRACK_SELECTOR) : null;
        if (!track) return;

        chipTooltips(track)?.tooltip('hide');
        updateScrollState(track.closest(PICKER_SELECTOR));
    }, { capture: true, passive: true });

    document.addEventListener('wheel', (event) => {
        const track = event.target.closest(TRACK_SELECTOR);
        if (!track) return;

        const delta = wheelDelta(event, track);
        const maxScroll = track.scrollWidth - track.clientWidth;
        const canScroll = delta < 0 ? track.scrollLeft > 0 : track.scrollLeft < maxScroll;
        if (!canScroll) return;

        event.preventDefault();
        queueWheelScroll(track, delta);
    }, { passive: false });

    window.addEventListener('resize', () => {
        document.querySelectorAll(PICKER_SELECTOR).forEach(updateScrollState);
    });

    new MutationObserver(() => renderPending(document)).observe(document.body, { childList: true, subtree: true });
    renderPending(document);
    document.querySelectorAll(`${PICKER_SELECTOR} ${TRACK_SELECTOR}`).forEach(observeTrack);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSavedCardPicker);
} else {
    initSavedCardPicker();
}
